import { mdiLoading } from '@mdi/js';
import Icon from '@mdi/react';
import iziToast from 'izitoast';
import { PenIcon, Zap } from 'lucide-react';
import { useContext, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import Permission from 'src/components/shared/Permission';
import { TextAreaTranslateComponent } from 'src/components/shared/TextAreaTranslateComponent';
import { AppAgentContext } from 'src/context/ContextProvider';
import { uploadFileCompressedService } from 'src/services/AssetService';
import { appActions } from 'src/store';
import { ModalType } from 'src/types/Modal';
import { AiAgentV2Data } from '../../../../types/AiAgentV2';
import { AI_AGENT_V2_EDIT } from '../../../../types/Permission';
import { grantPermission, temporallyLabelOpenAiToOracle } from '../../../../utils';
import RenderFormFieldComponent from '../../../shared/renderFormFieldsComponent/RenderFormFieldComponent';
import RenderFormFieldsComponent, {
    GroupTypes,
} from '../../../shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { chatbot } from '../AgentLayoutComponentV2';
import { AgentSetupContext } from '../AgentSetupComponent';

const classNameLabelAgent = '!text-purple-700 !font-semibold';
const classNameInput =
    '!border-purple-300 !focus:border-purple-500 !focus:ring !focus:ring-purple-200 !focus:ring-opacity-50';
const AgentComponent = () => {
    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');
    const { t: report } = useTranslate('report');
    const { control, modelOptions, getValues, setValue, watch } = useContext(AgentSetupContext);
    const [isLoading, setIsLoading] = useState(false);
    const inputAvatarRef = useRef<HTMLInputElement>(null);
    const { id } = useParams();

    const {
        state: {
            session: { ai_agent_config, permissions },
        },
        dispatch,
    } = useContext(AppAgentContext);

    const allowEdit = useMemo(() => {
        return grantPermission(AI_AGENT_V2_EDIT, permissions);
    }, [permissions]);

    const agentFields = useMemo((): GroupTypes<keyof AiAgentV2Data> => {
        return {
            agent1: {
                columns: 'grid-cols-1 mb-3',
                items: [
                    {
                        name: 'name',
                        label: common('name'),
                        classNameLabel: classNameLabelAgent,
                        classNameInput,
                        placeholder: common('name'),
                        disabled: id !== 'create' && !allowEdit,
                        rules: {
                            required: true,
                        },
                    },
                ],
            },
        };
    }, [modelOptions]);

    const newModelOptions = useMemo(() => {
        return modelOptions?.map((option) => ({
            ...option,
            label:
                ai_agent_config?.agent_setup?.models?.find((item) => item.value === option.value)?.label ||
                temporallyLabelOpenAiToOracle(option.label),
        }));
    }, [modelOptions]);

    const srcImage = useMemo(() => {
        const avatar = getValues().avatar;
        if (avatar) {
            return avatar;
        } else return chatbot;
    }, [getValues().avatar]);

    const onChangeAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const files = [e.target.files[0]];
            setIsLoading(true);
            const res = await uploadFileCompressedService(ai_agent_config.folder_key_avatars, files);

            if (res.success) {
                const name = res.items[0].name;
                setValue('avatar', `${ai_agent_config.path_avatars}/${name}`);
                iziToast.success({
                    message: common('image_uploaded'),
                    position: 'topCenter',
                });
                setIsLoading(false);
            } else {
                iziToast.warning({
                    message: common('error_uploading_image'),
                    position: 'topCenter',
                });
                setIsLoading(false);
            }
        }
    };

    const openAssetsModal = () => {
        dispatch(
            appActions.openModal({
                type: ModalType.ASSETS_AGENT,
                data: {
                    title: common('select_image'),
                    setData: (item) => setValue('avatar', item),
                    onClose: () => dispatch(appActions.closeModal(ModalType.ASSETS_AGENT)),
                },
            })
        );
    };

    return (
        // <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-lg">
        <Card className="bg-gradient-to-br from-pink-100 to-purple-100 shadow-xl">
            <CardHeader>
                <CardTitle className="text-2xl font-bold text-purple-800">{agent('agent_settings')}</CardTitle>
            </CardHeader>

            <CardContent className=" flex flex-col">
                <div className="grid grid-cols-1 lg:grid-cols-[70%_30%] gap-6 mb-5">
                    {Object.keys(agentFields).map((group) => {
                        const currentGroup = agentFields[group];

                        return (
                            <div className={`flex flex-col`} key={group}>
                                {currentGroup.label && currentGroup.label}
                                <div className={`grid ${currentGroup.columns ?? 'grid-cols-1 '} gap-3 mb-3`}>
                                    <RenderFormFieldsComponent fields={currentGroup.items} control={control} />
                                </div>
                            </div>
                        );
                    })}

                    <div className="flex gap-2 items-center mb-5">
                        <label htmlFor="" className="block text-sm  leading-6 !text-purple-700 !font-semibold">
                            Avatar
                        </label>
                        <div className="py-2 px-4 flex gap-2">
                            <input
                                className="hidden"
                                type="file"
                                accept="image/png, image/jpeg"
                                onChange={onChangeAvatar}
                                ref={inputAvatarRef}
                            />
                            <div className="relative">
                                <img src={srcImage} alt="avatar" width={60} className="rounded-full" />
                                <Permission permission={AI_AGENT_V2_EDIT}>
                                    <div
                                        className="bg-white rounded-full cursor-pointer bottom-[-6px] right-0 w-6 flex justify-center items-center shadow absolute"
                                        onClick={openAssetsModal}
                                    >
                                        <PenIcon width={12} />
                                    </div>
                                </Permission>
                            </div>
                            {isLoading && <Icon className="mr-1" size={1} path={mdiLoading} spin />}
                        </div>
                    </div>
                </div>

                <Accordion type="single" collapsible className="w-full" defaultValue={'additional-features'}>
                    <AccordionItem value="additional-features" className="border-b-0">
                        <AccordionTrigger className="accordion-trigger-label">
                            <div className="flex items-center capitalize">
                                <Zap className="w-5 h-5 mr-2" />
                                {report('advance_actions')}
                            </div>
                        </AccordionTrigger>
                        <AccordionContent className=" space-y-4">
                            <RenderFormFieldComponent
                                item={{
                                    rules: {
                                        required: true,
                                    },
                                    classNameLabel: classNameLabelAgent,
                                    label: common('model'),
                                    name: 'main_model',
                                    placeholder: common('model'),
                                    classNameInput,
                                    disabled: id !== 'create' && !allowEdit,
                                    options: newModelOptions || [],
                                    type: 'select',
                                }}
                                control={control}
                            />

                            {/* <RenderFormFieldComponent item={{
                                rules: {
                                    required: true,
                                },
                                classNameLabel: classNameLabelAgent,
                                label: agent('prompt'),
                                name: 'main_prompt',
                                placeholder: agent('agent_prompt_placeholder'),
                                classNameInput,
                                options: modelOptions,
                                type: 'textarea',
                            }} control={control} /> */}

                            <TextAreaTranslateComponent
                                originalPlaceholder={agent('agent_prompt_placeholder')}
                                translatePlaceholder={agent('agent_prompt_placeholder')}
                                keyFormOriginal="main_prompt"
                                keyFormTranslate="main_prompt_en"
                                label={agent('prompt')}
                                getValues={getValues}
                                setValue={setValue}
                                watch={watch}
                                disabled={id !== 'create' && !allowEdit}
                            />
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </CardContent>
        </Card>
    );
};

export default AgentComponent;
